import React from 'react';
import { BookOpen, Users, CheckCircle, ChevronRight } from 'lucide-react';
import { trackStudentCTA, trackTeacherCTA } from '../../services/analytics';

const ChoosePath = ({ isDarkMode, onGetStarted, onScrollToSection }) => {
  const handleStudentClick = () => {
    trackStudentCTA('choose_path');
    onGetStarted('student');
  };

  const handleTeacherClick = () => {
    trackTeacherCTA('choose_path');
    onGetStarted('teacher');
  };

  return (
    <section data-testid="section-choose-path" id="choose-path" className="py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className={`inline-block text-xs uppercase tracking-[0.25em] font-bold mb-4 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>Choose Your Path</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-['Outfit'] tracking-tight mb-4">
            Built for How You Learn & Teach
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {/* Student Card */}
          <div 
            data-testid="path-card-student"
            className={`group relative rounded-2xl p-6 sm:p-8 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 cursor-pointer ${isDarkMode ? 'bg-[#131C2E] border border-slate-800 hover:border-sky-600' : 'bg-white border border-slate-200 hover:border-sky-400'}`}
            onClick={() => onScrollToSection('students-section')}
          >
            <div className="absolute top-0 left-0 w-full h-1 rounded-t-2xl bg-gradient-to-r from-sky-400 to-sky-600" />
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4 ${isDarkMode ? 'bg-sky-900/50 text-sky-400' : 'bg-sky-100 text-sky-700'}`}>
              <BookOpen className="w-4 h-4" />
              For Students
            </div>
            <h3 className="text-xl sm:text-2xl font-bold font-['Outfit'] mb-3">Self-Study & Exam Prep</h3>
            <p className={`mb-6 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              Upload any study material and instantly test your knowledge. Practice mistakes until you master every concept.
            </p>
            <ul className="space-y-3 mb-6">
              {['Instant AI-powered quiz generation', 'Step-by-step solution solver', 'Smart mistake practice', 'Exam-style presets (NECTA, IGCSE, SAT...)'].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className={`w-5 h-5 flex-shrink-0 ${isDarkMode ? 'text-sky-400' : 'text-sky-600'}`} />
                  <span className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{item}</span>
                </li>
              ))}
            </ul>
            <button 
              onClick={(e) => { e.stopPropagation(); handleStudentClick(); }}
              data-testid="path-cta-student"
              className="w-full py-3 bg-gradient-to-r from-sky-500 to-sky-600 text-white rounded-xl font-bold hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2"
            >
              Start Learning <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Teacher Card */}
          <div 
            data-testid="path-card-teacher"
            className={`group relative rounded-2xl p-6 sm:p-8 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 cursor-pointer ${isDarkMode ? 'bg-[#131C2E] border border-slate-800 hover:border-emerald-600' : 'bg-white border border-slate-200 hover:border-emerald-400'}`}
            onClick={() => onScrollToSection('teachers-section')}
          >
            <div className="absolute top-0 left-0 w-full h-1 rounded-t-2xl bg-gradient-to-r from-emerald-400 to-emerald-600" />
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4 ${isDarkMode ? 'bg-emerald-900/50 text-emerald-400' : 'bg-emerald-100 text-emerald-700'}`}>
              <Users className="w-4 h-4" />
              For Teachers
            </div>
            <h3 className="text-xl sm:text-2xl font-bold font-['Outfit'] mb-3">Classroom Assessment</h3>
            <p className={`mb-6 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              Convert lesson plans into assessments in seconds. Track class performance and identify areas needing attention.
            </p>
            <ul className="space-y-3 mb-6">
              {['Convert any lesson into quizzes', 'Share via link, PDF, or WhatsApp', 'Real-time class analytics', 'Automated grading & feedback'].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className={`w-5 h-5 flex-shrink-0 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                  <span className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{item}</span>
                </li>
              ))}
            </ul>
            <button 
              onClick={(e) => { e.stopPropagation(); handleTeacherClick(); }}
              data-testid="path-cta-teacher"
              className="w-full py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl font-bold hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2"
            >
              Start Teaching <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ChoosePath;
